/* eslint-disable max-lines */
import enc from "crypto-js/enc-hex";
import SHA256 from "crypto-js/sha256";
import addMonths from "date-fns/addMonths";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import type { NextPageContext, PageProps } from "next";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { scmpPlus } from "scmp-sdk";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { data as appData, article as articleData } from "shared/data";
import { section as sectionData } from "shared/data/section";
import { Edition } from "shared/lib/edition";
import { theme } from "shared/lib/styles";
import { notEmpty, parseQueryString } from "shared/lib/utils";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "scmp-app/components/advertisement/ad-slots/types";
import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { HomeAdvertisers } from "scmp-app/components/home/advertisers";
import { AiArticleList } from "scmp-app/components/home/ai-article-list";
import { HomeAround } from "scmp-app/components/home/around";
import { HomeChinaFutureTechStoryPackage } from "scmp-app/components/home/china-future-tech-story-package";
import { HomeClientQueryLoader } from "scmp-app/components/home/client-query-loader";
import { HomeFocusArticle } from "scmp-app/components/home/focus-article";
import { HomeHeroArticle } from "scmp-app/components/home/hero-article";
import { HomeHeroThreeArticles } from "scmp-app/components/home/hero-three-articles";
import { HomeLatest } from "scmp-app/components/home/latest";
import { MyNewsDailyFive } from "scmp-app/components/home/my-news-daily-five";
import { HomeNewsletter } from "scmp-app/components/home/newsletter";
import { HomepageClientQueryPlaceholder } from "scmp-app/components/home/placeholder";
import { HomepageAroundScmpPlaceholder } from "scmp-app/components/home/placeholder/around-scmp-placeholder";
import { HomeSecondaryStoryPackage } from "scmp-app/components/home/secondary-story-package";
import { HomeSectionTop } from "scmp-app/components/home/section-top";
import { HomeSeo } from "scmp-app/components/home/seo";
import { HomeSeriesWidget } from "scmp-app/components/home/series-widget";
import { HomeSpotlight } from "scmp-app/components/home/spotlight";
import { HomeSuperSplash } from "scmp-app/components/home/super-splash";
import { HomeThingsToDo } from "scmp-app/components/home/things-to-do";
import { HomeTopStories } from "scmp-app/components/home/top-stories";
import { MostPopular as MostPopularWidget } from "scmp-app/components/most-popular";
import { Multimedia as MultimediaWidget } from "scmp-app/components/multimedia";
import { PlusWidget } from "scmp-app/components/plus/plus-widget";
import { ChinaScienceDiscoveryWidget } from "scmp-app/components/rhs-module/china-science-discovery-widget";
import { SubscriptionWidget } from "scmp-app/components/subscription-widget";
import { TrendingTopic } from "scmp-app/components/trending-topic";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import {
  useAutoRefreshWhenIdlingForTenMinutes,
  useGetEntityIdsByQueueItems,
} from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type {
  homePageQuery,
  LiveWidgetEditionEnum,
} from "scmp-app/queries/__generated__/homePageQuery.graphql";

import { DesktopMaximumAdHeight } from "./consts";
import { getHomePageLayoutVariant } from "./helpers";
import {
  useLayoutWorkaroundForMyNews,
  usePickMorningStudioArticles,
  useWhyRegisterHomepagePositionTriggerReference,
} from "./hooks";
import {
  AdContainer,
  AroundScmp,
  Comment,
  Container,
  ContentContainer,
  ContentWrapper,
  EighthAds,
  FifthAds,
  FifthSectionBlock,
  FirstAds,
  FirstSectionBlock,
  Focus,
  FourthAds,
  FourthSectionBlock,
  FromOurAdvertisers,
  HeroArticles,
  HeroThreeArticles,
  IpuNewsletter,
  Latest,
  LayoutVariant,
  MostPopular,
  Multimedia,
  MyNews,
  Newsletters,
  NinthAds,
  Plus,
  RecommendedForYou,
  SecondAds,
  SecondSectionBlock,
  SectionBlocksAndSpotlight,
  SeventhAds,
  SixthAds,
  StyledCommentWidget,
  StyledTakeoverAdSlot,
  Subscription,
  ThingsToDo,
  ThirdAds,
  ThirdSectionBlock,
  TopStories,
  YouMayHaveMissed,
} from "./styles";

const query = graphql`
  query homePageQuery(
    $applicationId: String!
    $chinaFutureTechSectionEntityId: String!
    $chinaFutureTechStoryPackageArticlesQueueName: String!
    $commentHarrySectionIds: [String]
    $commentQueueName: String!
    $dailyPulseTypeUuId: String!
    $focusQueueName: String!
    $highlightQueueName: String!
    $latestExcludeArticleTypes: [String]
    $latestExcludeSectionIds: [String]
    $liveWidgetEditionEnumValue: LiveWidgetEditionEnum!
    $morningStudioQueueName: String!
    $plusNewsAgendaEndDate: Timestamp!
    $plusNewsAgendaStartDate: Timestamp!
    $plusNewsAgendaTypes: [String]!
    $scmpPlusPaywallTypeIds: [String]
    $spotlightQueueName: String!
    $storyPackageArticlesQueueName: String!
    $secondaryStoryPackageArticlesQueueName: String!
    $seriesWidgetArticlesQueueName: String!
    $topStoriesQueueName: String!
  ) {
    topStories: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          ...heroThreeArticlesQueueItemsEdge
          ...topStoriesQueueItemsEdge
          node {
            ...heroArticleArticle
          }
        }
      }
    }
    morningStudioQueue: queue(filter: { name: $morningStudioQueueName }) {
      ...hooksPickMorningStudioArticle
    }
    ...latestContentsQuery
      @arguments(
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        latestExcludeSectionIds: $latestExcludeSectionIds
        latestExcludeArticleTypes: $latestExcludeArticleTypes
      )
    ...thingsToDoQueue
    ...commentQueueQuery
      @arguments(
        commentQueueName: $commentQueueName
        commentHarrySectionIds: $commentHarrySectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
    ...chinaFutureTechStoryPackageQuery
      @arguments(
        sectionEntityId: $chinaFutureTechSectionEntityId
        queueName: $chinaFutureTechStoryPackageArticlesQueueName
      )
    ...secondaryStoryPackageQuery
      @arguments(secondaryStoryPackageArticlesQueueName: $secondaryStoryPackageArticlesQueueName)
    ...storyPackageQuery @arguments(storyPackageArticlesQueueName: $storyPackageArticlesQueueName)
    ...seriesWidgetQuery @arguments(seriesWidgetArticlesQueueName: $seriesWidgetArticlesQueueName)
    ...spotlightQuery @arguments(spotlightQueueName: $spotlightQueueName)
    ...focusArticleHomeFocusArticleQuery @arguments(focusQueueName: $focusQueueName)
    ...superSplashQuery @arguments(edition: $liveWidgetEditionEnumValue)
    ...plusWidgetQuery
      @arguments(
        applicationId: $applicationId
        dailyPulseTypeUuId: $dailyPulseTypeUuId
        highlightQueueName: $highlightQueueName
        plusNewsAgendaEndDate: $plusNewsAgendaEndDate
        plusNewsAgendaStartDate: $plusNewsAgendaStartDate
        plusNewsAgendaTypes: $plusNewsAgendaTypes
      )
    ...chinaScienceDiscoveryWidgetQuery

    commentQueue: queue(filter: { name: $commentQueueName }) {
      items(first: 5) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    topStories: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    storyPackageArticles: queue(filter: { name: $storyPackageArticlesQueueName }) {
      items(first: 3) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    spotlightQueue: queue(filter: { name: $spotlightQueueName }) {
      items(first: 4) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    secondaryStoryPackageArticles: queue(
      filter: { name: $secondaryStoryPackageArticlesQueueName }
    ) {
      items(first: 4) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
  }
`;

type HomePageComponentProps = HomepagePageProps & PageProps;

type HomepagePageProps = {
  layoutVariant?: LayoutVariant;
};

const HomePage: RelayComponent<HomePageComponentProps, homePageQuery> = ({
  layoutVariant,
  preloadedQuery,
  randomSeed,
}) => {
  const { query: routeQuery } = useRouter();
  const edition = routeQuery.edition as Edition;
  const isAsiaEdition = edition === Edition.Asia;
  const isHongKongEdition = edition === Edition.HongKong;
  const data = usePreloadedQuery(query, preloadedQuery);

  const adZone = useMemo(() => `homepage_${edition}/home`, [edition]);

  useAutoRefreshWhenIdlingForTenMinutes();

  const [
    heroArticle,
    heroThreeArticle1,
    heroThreeArticle2,
    heroThreeArticle3,
    topStoryArticle1,
    topStoryArticle2,
    topStoryArticle3,
    ...rest
  ] = data?.topStories?.items?.edges?.filter(item => notEmpty(item)) ?? [];

  const [morningStudioArticleSlot1, morningStudioArticleSlot2] = usePickMorningStudioArticles(
    randomSeed ?? "fallback seed",
    data.morningStudioQueue,
  );

  const heroThreeArticles = [heroThreeArticle1, heroThreeArticle2, heroThreeArticle3];

  const topStories = [
    topStoryArticle1,
    topStoryArticle2,
    topStoryArticle3,
    morningStudioArticleSlot1,
    ...rest,
  ];

  const { isSecondSectionHigherThanMyNews, setMyNewsElement, setSecondSectionBlockElement } =
    useLayoutWorkaroundForMyNews(edition);

  const whyRegisterHomepagePositionTriggerReference =
    useWhyRegisterHomepagePositionTriggerReference();

  const ssrContentEntityIds = useGetEntityIdsByQueueItems([
    ...(data.spotlightQueue?.items?.edges ?? []),
    ...(data.commentQueue?.items?.edges ?? []),
    ...(data.topStories?.items?.edges ?? []),
    ...(data.storyPackageArticles?.items?.edges ?? []),
    ...(data.secondaryStoryPackageArticles?.items?.edges ?? []),
  ]);

  const fourthSectionBlockSection = useMemo(() => {
    if (isAsiaEdition) {
      return "lifestyle";
    }
    if (isHongKongEdition) {
      return "wellness";
    }
    return "people-culture";
  }, [isAsiaEdition, isHongKongEdition]);

  return (
    <>
      <HomeSeo edition={edition} />
      <Container>
        <ContentContainer>
          <HomeSuperSplash reference={data} />
          <ContentWrapper
            $edition={edition}
            $isSecondSectionHigherThanMyNews={isSecondSectionHigherThanMyNews}
            $layoutVariant={layoutVariant}
          >
            <IpuNewsletter>
              <AdSlot
                adUnit="d_banner3"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[[300, 50]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                zone={adZone}
              />
            </IpuNewsletter>
            <HeroArticles>
              {heroArticle && <HomeHeroArticle reference={heroArticle.node} />}
            </HeroArticles>
            <FirstAds>
              <AdContainer>
                <AdSlot
                  adUnit="d_lrec1"
                  breakpoint={theme.breakpoints.up("desktop")}
                  sizes={[[300, 250]]}
                  targeting={{
                    paid: TargetingPaidType.NotArticle,
                  }}
                  withLabel
                  zone={adZone}
                />
                <AdSlot
                  adUnit="m_lrec1"
                  breakpoint={theme.breakpoints.only("tablet")}
                  sizes={[[300, 250]]}
                  targeting={{
                    paid: TargetingPaidType.NotArticle,
                  }}
                  withLabel
                  zone={adZone}
                />
                <AdSlot
                  adUnit="m_banner1"
                  breakpoint={theme.breakpoints.only("mobile")}
                  sizes={[
                    [300, 50],
                    [320, 50],
                    [300, 100],
                    [320, 100],
                    [300, 250],
                  ]}
                  targeting={{
                    paid: TargetingPaidType.NotArticle,
                  }}
                  withLabel
                  zone={adZone}
                />
              </AdContainer>
            </FirstAds>
            <HeroThreeArticles>
              {heroThreeArticles.length > 0 && (
                <HomeHeroThreeArticles reference={heroThreeArticles} />
              )}
            </HeroThreeArticles>
            <Focus>
              <HomeFocusArticle pageType="homepage" reference={data} />
            </Focus>
            <TopStories>
              <HomeTopStories adZone={adZone} reference={topStories} />
            </TopStories>
            <Plus>
              <PlusWidget
                fallbackWidget={() => (
                  <ChinaScienceDiscoveryWidget desktopItemCount={3} reference={data} />
                )}
                pageType="homepage"
                reference={data}
                sampleContent="homepage"
              />
            </Plus>
            <Comment>
              <StyledCommentWidget reference={data} />
            </Comment>
            <Latest>
              <HomeLatest reference={data} />
            </Latest>
            <ThingsToDo $edition={edition}>
              {edition === Edition.HongKong && <HomeThingsToDo reference={data} />}
            </ThingsToDo>
            <SecondAds>
              <AdSlot
                adUnit="d_lrec3"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, DesktopMaximumAdHeight],
                ]}
                stickySpacing={60}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />
              <AdSlot
                adUnit="m_lrec3"
                breakpoint={theme.breakpoints.only("tablet")}
                sizes={[
                  [300, 250],
                  [320, 480],
                ]}
                stickySpacing={60}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />
            </SecondAds>
            <ThirdAds>
              <AdSlot
                adUnit="d_lrec3a"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                zone={adZone}
              />
              <AdSlot
                adUnit="m_lrec3a"
                breakpoint={theme.breakpoints.only("tablet")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                zone={adZone}
              />
            </ThirdAds>
            <Newsletters>
              <HomeNewsletter />
            </Newsletters>
            <SectionBlocksAndSpotlight>
              <HomeSeriesWidget reference={data} />
              <HomeSecondaryStoryPackage reference={data} />
              <HomeChinaFutureTechStoryPackage reference={data} />
              <div ref={whyRegisterHomepagePositionTriggerReference}>
                <HomeSpotlight reference={data} />
              </div>
            </SectionBlocksAndSpotlight>
            <FourthAds>
              <AdSlot
                adUnit="d_lrec2"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, DesktopMaximumAdHeight],
                ]}
                stickySpacing={60}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />
              <AdSlot
                adUnit="m_lrec2"
                breakpoint={theme.breakpoints.only("tablet")}
                sizes={[
                  [300, 250],
                  [320, 480],
                ]}
                stickySpacing={60}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />

              <AdContainer>
                <AdSlot
                  adUnit="m_lrec3"
                  breakpoint={theme.breakpoints.only("mobile")}
                  sizes={[
                    [300, 250],
                    [320, 480],
                  ]}
                  targeting={{
                    paid: TargetingPaidType.NotArticle,
                  }}
                  withLabel
                  zone={adZone}
                />
              </AdContainer>
            </FourthAds>

            <ErrorBoundary fallback={<AppFooter />}>
              <ClientSideSuspense fallback={<HomepageClientQueryPlaceholder />}>
                <HomeClientQueryLoader ssrContentEntityIds={ssrContentEntityIds}>
                  {({ aiWidgets, data, existingEntityIds }) => (
                    <>
                      <Multimedia>
                        <MultimediaWidget reference={data} />
                      </Multimedia>
                      <RecommendedForYou>
                        {aiWidgets[AiEngineDataType.HomeRecommendedForYou].entityIds.length > 0 && (
                          <AiArticleList
                            aiEngineResponse={aiWidgets[AiEngineDataType.HomeRecommendedForYou]}
                            sponsoredReference={morningStudioArticleSlot2.node}
                            variant={AiEngineDataType.HomeRecommendedForYou}
                          />
                        )}
                      </RecommendedForYou>
                      <FirstSectionBlock>
                        {isAsiaEdition ? (
                          <HomeSectionTop reference={data} section="twia" />
                        ) : (
                          <HomeSectionTop reference={data} section="hong-kong" />
                        )}
                      </FirstSectionBlock>
                      <Subscription>
                        <SubscriptionWidget />
                      </Subscription>
                      <SecondSectionBlock>
                        {isAsiaEdition ? (
                          <HomeSectionTop
                            ref={element => setSecondSectionBlockElement?.(element)}
                            reference={data}
                            section="people-culture"
                          />
                        ) : (
                          <HomeSectionTop
                            ref={element => setSecondSectionBlockElement?.(element)}
                            reference={data}
                            section="lifestyle"
                          />
                        )}
                      </SecondSectionBlock>
                      <YouMayHaveMissed>
                        {aiWidgets[AiEngineDataType.HomeYouMayHaveMissed].entityIds.length > 0 && (
                          <AiArticleList
                            aiEngineResponse={aiWidgets[AiEngineDataType.HomeYouMayHaveMissed]}
                            variant={AiEngineDataType.HomeYouMayHaveMissed}
                          />
                        )}
                      </YouMayHaveMissed>
                      <MyNews $edition={edition}>
                        <MyNewsDailyFive ref={element => setMyNewsElement?.(element)} />
                      </MyNews>
                      <ThirdSectionBlock>
                        <HomeSectionTop reference={data} section="china" />
                      </ThirdSectionBlock>
                      <FourthSectionBlock>
                        <HomeSectionTop reference={data} section={fourthSectionBlockSection} />
                      </FourthSectionBlock>
                      <FifthSectionBlock>
                        <HomeSectionTop reference={data} section="style" />
                      </FifthSectionBlock>
                      <MostPopular>
                        <MostPopularWidget
                          reference={data}
                          trendingTopicSlot={<TrendingTopic reference={data} />}
                        />
                      </MostPopular>
                      <FromOurAdvertisers>
                        {data?.advertisers?.items && (
                          <HomeAdvertisers reference={data?.advertisers?.items} />
                        )}
                      </FromOurAdvertisers>
                      {/* For preventing AroundScmp is too slow and blocking the rendering  */}
                      <ClientSideSuspense
                        fallback={
                          <>
                            <AroundScmp>
                              <HomepageAroundScmpPlaceholder />
                            </AroundScmp>
                          </>
                        }
                      >
                        <AroundScmp>
                          <HomeAround existingEntityIds={existingEntityIds} />
                        </AroundScmp>
                        <AppFooter />
                      </ClientSideSuspense>
                    </>
                  )}
                </HomeClientQueryLoader>
                <FifthAds>
                  <AdContainer>
                    <AdSlot
                      adUnit="d_lrec4"
                      breakpoint={theme.breakpoints.up("desktop")}
                      sizes={[[300, 250]]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />
                    <AdSlot
                      adUnit="m_lrec4"
                      breakpoint={theme.breakpoints.only("tablet")}
                      sizes={[[300, 250]]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />
                    <AdSlot
                      adUnit="m_lrec3c"
                      breakpoint={theme.breakpoints.only("mobile")}
                      sizes={[
                        [300, 250],
                        [320, 480],
                      ]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </FifthAds>
                <SixthAds>
                  {edition !== Edition.HongKong && (
                    <>
                      <AdSlot
                        adUnit="d_lrec4a"
                        breakpoint={theme.breakpoints.up("desktop")}
                        sizes={[
                          [300, 250],
                          [300, DesktopMaximumAdHeight],
                        ]}
                        stickySpacing={60}
                        targeting={{
                          paid: TargetingPaidType.NotArticle,
                        }}
                        withBackground
                        withLabel
                        withSticky
                        zone={adZone}
                      />
                      <AdSlot
                        adUnit="m_lrec4a"
                        breakpoint={theme.breakpoints.only("tablet")}
                        sizes={[
                          [300, 250],
                          [320, 480],
                        ]}
                        stickySpacing={60}
                        targeting={{
                          paid: TargetingPaidType.NotArticle,
                        }}
                        withBackground
                        withLabel
                        withSticky
                        zone={adZone}
                      />
                    </>
                  )}
                  <AdContainer>
                    {edition === Edition.HongKong && (
                      <>
                        <AdSlot
                          adUnit="d_lrec4a"
                          breakpoint={theme.breakpoints.up("desktop")}
                          sizes={[[300, 250]]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withLabel
                          zone={adZone}
                        />
                        <AdSlot
                          adUnit="m_lrec4a"
                          breakpoint={theme.breakpoints.only("tablet")}
                          sizes={[
                            [300, 250],
                            [320, 480],
                          ]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withLabel
                          zone={adZone}
                        />
                      </>
                    )}

                    <AdSlot
                      adUnit="m_lrec2"
                      breakpoint={theme.breakpoints.only("mobile")}
                      sizes={[
                        [300, 250],
                        [320, 480],
                      ]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </SixthAds>
                <SeventhAds>
                  <AdContainer>
                    <AdSlot
                      adUnit="d_lrec4d"
                      breakpoint={theme.breakpoints.up("desktop")}
                      sizes={[[300, 250]]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />

                    <AdSlot
                      adUnit="m_lrec4d"
                      breakpoint={theme.breakpoints.only("tablet")}
                      sizes={[[300, 250]]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />

                    <AdSlot
                      adUnit="m_lrec3g"
                      breakpoint={theme.breakpoints.only("mobile")}
                      sizes={[
                        [300, 250],
                        [320, 480],
                      ]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </SeventhAds>
                <EighthAds>
                  <AdContainer>
                    <AdSlot
                      adUnit="d_lrec4b"
                      breakpoint={theme.breakpoints.up("desktop")}
                      sizes={[[300, 250]]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />
                    <AdSlot
                      adUnit="m_lrec4b"
                      breakpoint={theme.breakpoints.only("tablet")}
                      sizes={[[300, 250]]}
                      targeting={{
                        paid: TargetingPaidType.NotArticle,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </EighthAds>
                <NinthAds>
                  <AdSlot
                    adUnit="m_lrec3h"
                    breakpoint={theme.breakpoints.only("mobile")}
                    sizes={[
                      [300, 250],
                      [320, 480],
                    ]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withLabel
                    zone={adZone}
                  />
                </NinthAds>
                <StyledTakeoverAdSlot adSlotProps={{ zone: adZone }} variant="home" />
              </ClientSideSuspense>
            </ErrorBoundary>
          </ContentWrapper>
        </ContentContainer>
      </Container>
    </>
  );
};

HomePage.displayName = "HomePage";

const getPageProps = (context: NextPageContext) => {
  const edition = parseQueryString(context.query.edition) as Edition | undefined;
  const adZone = `homepage_${edition ?? "int"}/home`;

  const pageProps: PageProps = {
    appBarConfiguration: {
      desktopBreakpoint: "desktop",
      hasDesktopAd: true,
      hasMobileAd: true,
      topBannerAdSlot: {
        desktop: {
          adUnit: "d_banner1",
          targeting: {
            hpto: TargetingHomePageTakeOverType.Top,
            paid: TargetingPaidType.NotArticle,
          },
          zone: adZone,
        },
        mobile: {
          adUnit: "m_banner3",
          targeting: {
            hpto: TargetingHomePageTakeOverType.Top,
            paid: TargetingPaidType.NotArticle,
          },
          zone: adZone,
        },
      },
      variant: "scmp/home",
    },
    appInitializerConfiguration: {
      hasTrendingNavBar: true,
    },
    initEdition: edition,
  };

  return pageProps;
};

const generateRandomSeed = () => SHA256(new Date().toString()).toString(enc);

export default withRelay(HomePage, query, {
  clientSideProps: context => ({
    ...getPageProps(context),
    layoutVariant: LayoutVariant.Default,
    randomSeed: generateRandomSeed(),
  }),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: async context => {
    const layoutVariant = await getHomePageLayoutVariant();
    const pageProps = Promise.resolve(getPageProps(context));
    return {
      ...(await pageProps),
      layoutVariant,
      randomSeed: generateRandomSeed(),
    };
  },
  variablesFromContext(context) {
    const edition = parseQueryString(context.query.edition) ?? "int";
    const liveWidgetEditionEnumValue: LiveWidgetEditionEnum =
      edition.toLocaleUpperCase() as Uppercase<Edition>;
    const startDate = startOfDay(new Date()).getTime();
    const endDate = addMonths(endOfDay(new Date()), 1).getTime();

    return {
      applicationId: appData.application.scmp.entityId,
      chinaFutureTechSectionEntityId: sectionData.chinaFutureTech.entityId,
      chinaFutureTechStoryPackageArticlesQueueName: `section_top_${sectionData.chinaFutureTech.entityId}`,
      commentHarrySectionIds: [sectionData.comment.harrysView.entityUuid],
      commentQueueName: `opinion_top_${edition}`,
      dailyPulseTypeUuId: articleData.types.dailyPulseBriefing.entityUuid,
      edition,
      focusQueueName: `homepage_focus_${edition}`,
      highlightQueueName: "scmp_plus_home_news",
      latestExcludeArticleTypes: [
        articleData.types.series.entityUuid,
        articleData.types.letters.entityUuid,
        articleData.types.recap.entityUuid,
      ],
      latestExcludeSectionIds: [
        sectionData.posties.entityUuid,
        sectionData.spark.entityUuid,
        sectionData.youngPost.entityUuid,
        sectionData.yp.entityUuid,
        sectionData.recap.entityUuid,
        sectionData.cooking.entityUuid,
      ],
      liveWidgetEditionEnumValue,
      morningStudioQueueName: `homepage_morning_studio_${edition}`,
      plusNewsAgendaEndDate: endDate,
      plusNewsAgendaStartDate: startDate,
      plusNewsAgendaTypes: [scmpPlus.topicType.newsAgenda],
      scmpPlusPaywallTypeIds: [scmpPlus?.article?.paywallTypeId],
      secondaryStoryPackageArticlesQueueName: `homepage_package_top_2_${edition}`,
      seriesWidgetArticlesQueueName: `homepage_series_widget_${edition}`,
      spotlightQueueName: `scmp_signatures_top_${edition}`,
      storyPackageArticlesQueueName: `homepage_package_top_${edition}`,
      topStoriesQueueName: `homepage_top_${edition}`,
    };
  },
});
